
import { useEffect } from "react"
import { Outlet } from "react-router-dom"
import { Layout } from "@/components/Layout"
import { MapPage } from "@/pages/MapPage"
import CommunityPage from "@/pages/CommunityPage"
import { ProfilePage } from "@/pages/ProfilePage"
import OAuth2RedirectHandler from "@/pages/auth/OAuth2RedirectHandler"
import NotFoundPage from "@/pages/NotFoundPage"
import PrivateRoute from "@/components/router/PrivateRoute"
import SiteRegistrationPage from "@/pages/site/SiteRegistrationPage"
import AddressSearchPage from "@/pages/site/AddressSearchPage"
import BookmarksPage from "@/pages/BookmarksPage"
import { useAppDispatch, useAppSelector } from "./app/hooks"
import {
  fetchUser,
  selectCurrentToken,
  setCredentials,
  setLoginSheetOpen,
} from "./features/auth/authSlice"
import LoginActionSheet from "@/components/auth/LoginActionSheet"
import { SiteDetailPage } from "@/pages/site/SiteDetailPage"
import PostCreatePage from "@/pages/PostCreatePage"
import PostDetailPage from "@/pages/PostDetailPage"
import { SettingsPage } from "@/pages/SettingsPage"

// 스타일 임포트
import "@/styles/Layout.css"
import "@/styles/SiteRegistrationPage.css"
import "@/styles/LoginActionSheet.css"

// App 컴포넌트는 이제 최상위 레이아웃 및 컨텍스트 제공자 역할을 합니다.
export const App = () => {
  const dispatch = useAppDispatch()
  const token = useAppSelector(selectCurrentToken)

  useEffect(() => {
    // 개발 모드일 경우 타이틀에 (개발) 접두사 추가
    if (
      import.meta.env.MODE !== "production" &&
      !document.title.startsWith("(개발)")
    ) {
      document.title = `(개발) ${document.title}`
    }
  }, [])

  useEffect(() => {
    if (token) {
      void dispatch(fetchUser())
    }
  }, [dispatch, token])

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const data = event.data as {
        type?: string
        payload?: { accessToken?: string; refreshToken?: string }
      }
      const { type, payload } = data
      if (type === "oauth-success" && payload) {
        const { accessToken, refreshToken } = payload
        if (accessToken && refreshToken) {
          dispatch(setCredentials({ accessToken, refreshToken }))
          dispatch(setLoginSheetOpen(false))
        }
      }
    }

    window.addEventListener("message", handleMessage)

    return () => {
      window.removeEventListener("message", handleMessage)
    }
  }, [dispatch])

  return (
    <>
      <Outlet />
      <LoginActionSheet />
    </>
  )
}

// 라우트 설정을 객체 배열로 정의하여 export합니다.
export const appRoutes = [
  {
    path: "/",
    element: <App />,
    children: [
      {
        element: <Layout />,
        children: [
          { index: true, element: <MapPage /> },
          { path: "profile", element: <ProfilePage /> },
          { path: "bookmarks", element: <BookmarksPage /> },
          { path: "community", element: <CommunityPage /> },
          { path: "community/:postId", element: <PostDetailPage /> },
          { path: "sites/:id", element: <SiteDetailPage /> },
          { path: "settings", element: <SettingsPage /> },
        ],
      },
      {
        path: "community/write",
        element: (
          <PrivateRoute>
            <PostCreatePage />
          </PrivateRoute>
        ),
      },
      {
        path: "site/register/:id?",
        element: (
          <PrivateRoute>
            <SiteRegistrationPage />
          </PrivateRoute>
        ),
      },
      {
        path: "site/address-search",
        element: <AddressSearchPage />,
      },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
  {
    path: "/oauth2/redirect",
    element: <OAuth2RedirectHandler />,
  },
]
